import React, { useEffect, useRef, useState } from "react";
import { Button, Empty, Input, Spin, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, LockOutlined, NumberOutlined, UserAddOutlined } from "@ant-design/icons";
import { useChat } from "./ChatContext";
import MessageBubble from "./MessageBubble";
import Composer from "./Composer";
import ReadReceiptTicks from "./ReadReceiptTicks";

const ThreadView: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const {
    selection,
    messages,
    messagesLoading,
    hasOlder,
    loadOlderMessages,
    sendMessage,
    myUserId,
    subscriptions,
    setInviteTarget,
    typingUsers,
    typingIntervals,
    sendTypingSignal,
    enableReadReceipts,
    readReceipts,
  } = useChat();

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevIdsRef = useRef<{ first?: number; last?: number }>({});
  const loadingOlderRef = useRef(false);
  const [newTopicName, setNewTopicName] = useState("");

  const isNewTopic = !!selection && selection.kind === "channel" && selection.topic === "";
  useEffect(() => {
    if (!isNewTopic) setNewTopicName("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) {
      prevIdsRef.current = {};
      return;
    }
    const first = messages[0].id;
    const last = messages[messages.length - 1].id;
    const prev = prevIdsRef.current;
    // Only jump to the bottom when the last message actually changed (a new
    // message arrived, or the thread just loaded) — never for an in-place
    // content patch on an existing message (a reaction toggle, a thumbnail
    // patch-in, a content edit), which leaves first/last untouched and
    // shouldn't move the reader's scroll position at all. This also
    // naturally excludes loading older history (prepended at the top: first
    // id shrinks, last id unchanged) — handleScroll's own rAF callback
    // restores the scroll offset for that case; jumping to bottom here
    // would fight it.
    if (prev.last === undefined || last !== prev.last) {
      el.scrollTop = el.scrollHeight;
    }
    prevIdsRef.current = { first, last };
  }, [messages]);

  if (!selection) {
    return (
      <div className="chat-thread chat-thread--empty">
        <Empty description="Select a channel or a conversation" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  const stream = selection.kind === "channel" ? subscriptions.find((s) => s.stream_id === selection.streamId) : null;
  // Remounts Composer (resetting its draft text + any in-flight typing
  // timers) whenever the selected thread changes, instead of carrying stale
  // "typing" state over to the new conversation.
  const selectionKey =
    selection.kind === "channel" ? `c:${selection.streamId}:${selection.topic}` : `d:${selection.userIds.join(",")}`;

  // Read-receipt ticks under the last message, but only when it's mine — the
  // same WhatsApp/Telegram convention as ConversationList's per-row marker.
  // Says nothing (rather than a "sent" single tick) here since it sits right
  // under a bubble the user just watched send; the DM list already covers
  // that "sent, not read yet" state. `readerIds` being undefined means it
  // hasn't been fetched yet (see ChatContext's poll effect) — stay silent
  // rather than guess.
  const readReceipt = (() => {
    if (!enableReadReceipts || myUserId === null || messages.length === 0) return null;
    const last = messages[messages.length - 1];
    if (last.sender_id !== myUserId) return null;
    const readerIds = readReceipts[last.id];
    if (!readerIds) return null;
    if (selection.kind === "dm") {
      const others = selection.userIds;
      return others.length > 0 && others.every((id) => readerIds.includes(id)) ? { tooltip: "Seen" } : null;
    }
    // Channel: there's no single "everyone" to wait for, so any reader at
    // all is worth surfacing — the count lives in a hover tooltip instead of
    // a permanent "Seen by N" label cluttering every message.
    return readerIds.length > 0 ? { tooltip: `Seen by ${readerIds.length}` } : null;
  })();

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !hasOlder || messagesLoading || loadingOlderRef.current) return;
    if (el.scrollTop >= 40) return;
    loadingOlderRef.current = true;
    const prevScrollHeight = el.scrollHeight;
    const prevScrollTop = el.scrollTop;
    loadOlderMessages().finally(() => {
      loadingOlderRef.current = false;
      // Runs after the browser has painted the prepended messages — restores
      // the same visual scroll position rather than jumping to the top/bottom.
      requestAnimationFrame(() => {
        const el2 = scrollRef.current;
        if (el2) el2.scrollTop = el2.scrollHeight - prevScrollHeight + prevScrollTop;
      });
    });
  };

  return (
    <div className="chat-thread">
      <div className="chat-thread__header">
        {onBack && <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={onBack} />}
        <div className="chat-thread__title">
          {selection.kind === "channel" ? (
            <>
              {stream?.invite_only ? <LockOutlined /> : <NumberOutlined />}
              <Typography.Text strong>{selection.streamName}</Typography.Text>
              {isNewTopic ? (
                <Input
                  size="small"
                  placeholder="Topic name…"
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  className="chat-thread__topic-input"
                />
              ) : (
                <Typography.Text type="secondary" className="chat-thread__topic">
                  {selection.topic}
                </Typography.Text>
              )}
            </>
          ) : (
            <Typography.Text strong>{selection.label}</Typography.Text>
          )}
        </div>
        {selection.kind === "channel" && stream && (
          <Button
            type="text"
            size="small"
            icon={<UserAddOutlined />}
            onClick={() => setInviteTarget(stream)}
            title="Invite people"
          />
        )}
      </div>

      <div className="chat-thread__messages-wrap">
        <div className="chat-thread__messages" ref={scrollRef} onScroll={handleScroll}>
          {messagesLoading && messages.length === 0 && (
            <div className="chat-thread__loading">
              <Spin size="small" />
            </div>
          )}
          {!messagesLoading && messages.length === 0 && (
            <Empty description="No messages yet — say hello" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              msg={m}
              mine={m.sender_id === myUserId}
              showSender={i === 0 || messages[i - 1].sender_id !== m.sender_id}
            />
          ))}
          {readReceipt && (
            <div className="chat-thread__receipt">
              <Tooltip title={readReceipt.tooltip}>
                <span className="chat-thread__receipt-tick">
                  <ReadReceiptTicks seen />
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {messagesLoading && messages.length > 0 && (
          <div className="chat-thread__messages-mask">
            <Spin size="small" />
          </div>
        )}
      </div>

      {typingUsers.length > 0 && (
        <div className="chat-thread__typing">
          {typingUsers.length === 1
            ? `${typingUsers[0]} is typing…`
            : typingUsers.length === 2
              ? `${typingUsers[0]} and ${typingUsers[1]} are typing…`
              : "Several people are typing…"}
        </div>
      )}

      <Composer
        key={selectionKey}
        onSend={(content) => sendMessage(content, isNewTopic ? newTopicName : undefined)}
        placeholder={
          isNewTopic
            ? "Start the conversation…"
            : selection.kind === "channel"
              ? `Message #${selection.streamName}`
              : `Message @${selection.label}`
        }
        onTypingStart={() => sendTypingSignal("start")}
        onTypingStop={() => sendTypingSignal("stop")}
        typingIntervals={typingIntervals}
      />
    </div>
  );
};

export default ThreadView;
